import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FlaskConical, Copy, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

// Advertiser test link: playable at any date / paused, anti-fraud off,
// plays stored apart (never in participants, stats or draws).
export default function TestLinkCard({ gameId, testIdPrefix = "test-link" }) {
  const { t } = useTranslation();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gameId) return;
    api
      .get(`/games/${gameId}/test-link`)
      .then((r) => setLink(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [gameId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success(t("dashboard.copied"));
    } catch {
      toast.error(t("game_editor.test_link_copy_error"));
    }
  };

  return (
    <div
      className="rounded-2xl border border-amber-500/40 bg-amber-500/[0.07] px-4 py-3.5"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center">
          <FlaskConical className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">
            {t("game_editor.test_link_title")}
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground leading-snug">
            {t("game_editor.test_link_hint")}
          </p>
          {error && (
            <div className="mt-2 text-xs text-destructive" data-testid={`${testIdPrefix}-error`}>
              {error}
            </div>
          )}
          {!link && !error && (
            <Loader2 className="mt-3 h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {link && (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <code
                className="flex-1 min-w-0 truncate rounded-lg bg-background/80 border border-border/60 px-3 py-2 text-xs font-mono"
                title={link.url}
                data-testid={`${testIdPrefix}-url`}
              >
                {link.url}
              </code>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-9"
                  onClick={copy}
                  data-testid={`${testIdPrefix}-copy-btn`}
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                  {t("game_editor.test_link_copy")}
                </Button>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-border h-9 px-3 text-sm hover:bg-muted/60"
                  data-testid={`${testIdPrefix}-open-btn`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("game_editor.test_link_open")}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
